import type {
  ApiContext,
  ApiNotAuthorized,
  CardDetail,
  CardListQuery,
  CardNotFound,
  CardPage,
  CardPricePoint,
  CardStateRefused,
  CardStockUpdate,
  InvalidFormField,
  StockLevel,
  StoreNotFound,
  StoreRoleForbidden,
} from "@hobbyshopify/backend-client";
import type { AsyncResult } from "operation-result.js";
import type { AuthHandler } from "trunk.js";
import type { BackendApi } from "./api";

/** The tab strip's vocabulary; `all` is the absent filter, not a value the backend stores. */
export type CardListFilter = NonNullable<CardListQuery["filter"]>;

type Api = { client: BackendApi; authHandler: AuthHandler<ApiContext> };

/** One page of the catalogue; `cursor` asks for the page after a previous answer's `nextCursor`. */
export function loadCards(
  o: Api & { shopId: string; filter: CardListFilter; search: string; cursor?: string },
): AsyncResult<CardPage, ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) =>
    o.client.cards.list(ctx, o.shopId, {
      filter: o.filter,
      search: o.search,
      ...(o.cursor ? { cursor: o.cursor } : {}),
    }),
  );
}

export function loadCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<CardDetail, ApiNotAuthorized | StoreNotFound | CardNotFound> {
  return o.authHandler.callApi((ctx) => o.client.cards.get(ctx, o.shopId, o.cardId));
}

/** A fresh market lookup. Resolves to the new price in cents, or null when nothing was found. */
export function repriceCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<number | null, ApiNotAuthorized | StoreNotFound | CardNotFound | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.reprice(ctx, o.shopId, o.cardId));
}

/** What the shop asks. Null takes the card off sale. */
export function setCardPrice(
  o: Api & { shopId: string; cardId: string; priceCents: number | null },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | InvalidFormField | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.setPrice(ctx, o.shopId, o.cardId, o.priceCents));
}

/** What the shop will not go below. Null clears the floor. */
export function setCardFloor(
  o: Api & { shopId: string; cardId: string; floorPriceCents: number | null },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | InvalidFormField | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.setFloor(ctx, o.shopId, o.cardId, o.floorPriceCents));
}

/** The new total on hand, or `trackStock: false` to stop counting. */
export function setCardStock(
  o: Api & { shopId: string; cardId: string; change: CardStockUpdate },
): AsyncResult<
  StockLevel,
  ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | InvalidFormField | StoreRoleForbidden
> {
  return o.authHandler.callApi((ctx) => o.client.cards.setStock(ctx, o.shopId, o.cardId, o.change));
}

export function archiveCard(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<void, ApiNotAuthorized | StoreNotFound | CardNotFound | CardStateRefused | StoreRoleForbidden> {
  return o.authHandler.callApi((ctx) => o.client.cards.archive(ctx, o.shopId, o.cardId));
}

/** The card's recorded prices, oldest first. */
export function loadPriceHistory(
  o: Api & { shopId: string; cardId: string },
): AsyncResult<CardPricePoint[], ApiNotAuthorized | StoreNotFound> {
  return o.authHandler.callApi((ctx) => o.client.cards.prices(ctx, o.shopId, o.cardId));
}
